import { Card, CardContent, Skeleton, toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { NumberField } from '../pricing-settings/billing-form-fields.js';

interface Limits {
  lotPerMinute: number;
  operatorPerMinute: number;
  lotPerDay: number;
}

interface Usage {
  day: string;
  lots: Array<{ parkingLotId: string; lotName: string | null; upstream: number; skipped: number }>;
}

const LIMITS_KEY = ['admin-plate-lookup-limits'];

const lookups = (n: number) => `${n.toLocaleString('pt-BR')} ${n === 1 ? 'consulta' : 'consultas'}`;

/**
 * Limites da consulta paga de placa (#660). Só a consulta que vai ao API Placas
 * conta: placa já guardada não gasta crédito e não tem limite. Quando o limite
 * segura, o ticket abre do mesmo jeito, só sem o modelo e a cor.
 */
export const PlateLookupsCard = () => {
  const qc = useQueryClient();
  const limits = useQuery({
    queryKey: LIMITS_KEY,
    queryFn: () => api.request<Limits>('/v1/admin/plate-lookups/settings'),
  });
  const usage = useQuery({
    queryKey: ['admin-plate-lookup-usage'],
    queryFn: () => api.request<Usage>('/v1/admin/plate-lookups/usage'),
  });
  const update = useMutation({
    mutationFn: (patch: Partial<Limits>) =>
      api.request<Limits>('/v1/admin/plate-lookups/settings', { method: 'PUT', json: patch }),
    onSuccess: (data) => {
      qc.setQueryData(LIMITS_KEY, data);
      toast.success('Limites de consulta atualizados.');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.'),
  });

  const l = limits.data;
  const lots = usage.data?.lots ?? [];

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div>
          <h2 className="text-sm font-semibold">Consulta de placa</h2>
          <p className="text-xs text-muted-fg">
            Teto de consultas pagas ao API Placas. Placa que já está guardada não conta. Quando o
            teto segura, o ticket abre normalmente, só sem o modelo e a cor do carro.
          </p>
        </div>
        {limits.isLoading || !l ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            <NumberField
              label="Por minuto, por estacionamento"
              value={l.lotPerMinute}
              unit="consultas"
              min={1}
              max={10_000}
              disabled={update.isPending}
              onSave={(v) => update.mutate({ lotPerMinute: v })}
            />
            <NumberField
              label="Por minuto, por operador"
              value={l.operatorPerMinute}
              unit="consultas"
              min={1}
              max={10_000}
              disabled={update.isPending}
              onSave={(v) => update.mutate({ operatorPerMinute: v })}
            />
            <NumberField
              label="Por dia, por estacionamento"
              value={l.lotPerDay}
              unit="consultas"
              min={1}
              max={1_000_000}
              disabled={update.isPending}
              onSave={(v) => update.mutate({ lotPerDay: v })}
            />
          </div>
        )}
        <div>
          <h3 className="text-xs font-semibold">Hoje</h3>
          {usage.isLoading ? (
            <Skeleton className="mt-2 h-8 w-full" />
          ) : lots.length === 0 ? (
            <p className="text-xs text-muted-fg">Nenhuma consulta paga hoje.</p>
          ) : (
            <ul className="divide-y divide-border">
              {lots.slice(0, 10).map((lot) => (
                <li
                  key={lot.parkingLotId}
                  className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
                >
                  <span className="min-w-0 truncate text-sm">
                    {lot.lotName ?? lot.parkingLotId}
                  </span>
                  <span className="shrink-0 text-xs text-muted-fg">
                    {lookups(lot.upstream)}
                    {lot.skipped > 0
                      ? ` · ${lot.skipped} ${lot.skipped === 1 ? 'segurada' : 'seguradas'} pelo limite`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
